import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Engine } from '@babylonjs/core/Engines/engine';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import { Scene } from '@babylonjs/core/scene';

import { cameraZoomOverride, logRenderFeatures, renderFeatures } from './compat';
import { attachPixelArt } from './pixelate';
import { attachColourGrade } from './grading';
import { attachSilhouetteOutline } from './outline';

// Side-effect imports. Babylon registers builders, picking and shadow rendering
// on import; without these the tree-shaken bundle throws at runtime instead of
// at build time.
// The builder half of this list is the one statement of which geometry this scene
// builds, and it is checked against the code by `engine.test.ts` rather than trusted.
// Two things were wrong with it: **`boxBuilder` was absent** while `loot.ts`,
// `zone/render.ts` and `models/rig.ts` had all been calling `MeshBuilder.CreateBox` for
// milestones, and `groundBuilder` was named although nothing has built a ground plane
// since the floor became the zone's tiled boxes.
//
// Neither broke anything today, because `Meshes/meshBuilder` imports every builder
// itself — so the list is currently documentation rather than a runtime guard. It stops
// being documentation the moment it is wrong, and it becomes load-bearing the moment a
// file moves to Babylon's tree-shakeable `CreateBox(...)` form, where a missing import
// *is* an undefined function with no build error.
import '@babylonjs/core/Culling/ray';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import '@babylonjs/core/Materials/standardMaterial';
import '@babylonjs/core/Meshes/Builders/boxBuilder';
import '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
// The Phase 41 rig: limbs are capsules, helms and crystals are turned, bow limbs are tubes.
import '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import '@babylonjs/core/Meshes/Builders/latheBuilder';
import '@babylonjs/core/Meshes/Builders/tubeBuilder';
// The town portal's gate was the only torus in the scene until Phase 41 made belts,
// brims and ribs rings too; it is still the only one you walk *through*.
import '@babylonjs/core/Meshes/Builders/torusBuilder';
// The portal's membrane, when the generated arch replaces the ring's look (Phase 42).
import '@babylonjs/core/Meshes/Builders/planeBuilder';
// The liquid beyond a caldera's or a marsh's rim (`ZoneRenderer.buildBeyond`).
import '@babylonjs/core/Meshes/Builders/groundBuilder';

// Shader sources, imported eagerly — and this is not optional.
//
// Babylon assembles a shader by resolving `#include<name>` against a registry
// that each shader module populates on import. Left to itself it loads those
// modules *dynamically*, so whether an include is registered before the first
// compile is a race. Lose it and Babylon hands the driver source with a literal
// `#include<...>` still in it, which fails as:
//
//     SHADER ERROR: 0:42: '<' : syntax error
//
// Babylon then silently skips every mesh whose material failed, so the entire
// world is black behind a working HUD — intermittently, depending on how the
// module loads raced that particular reload. Importing the shaders statically
// makes the registry complete before anything compiles.
//
// `default` is StandardMaterial; `shadowMap` is the ShadowGenerator's depth pass;
// `outline` is the hover highlight. Adding a material type, a post-process or a
// renderer here means adding its shader too.
//
// **`outline` was missing**, and it was the only shader in use that was. Babylon's
// OutlineRenderer fetches its own source through a dynamic `import()` inside
// `extraInitializationsAsync`, so `isReady()` is false until that resolves and the
// highlight simply does not draw for the frames in between — the same race this whole
// block exists to remove, and silent in exactly the same way. Verified by
// `engine.test.ts`, which asserts the registry contains every shader this scene can
// ask for.
import '@babylonjs/core/Shaders/default.vertex';
import '@babylonjs/core/Shaders/default.fragment';
import '@babylonjs/core/Shaders/shadowMap.vertex';
import '@babylonjs/core/Shaders/shadowMap.fragment';
// The glow layer's three programs. Same rule as the two above and the same failure if
// they are missed: Babylon loads a shader it has not been given dynamically, and racing
// that leaves a literal `#include<...>` in the GLSL — *intermittently*. A glow layer whose
// merge shader failed does not draw nothing, it draws the scene without the merge.
import '@babylonjs/core/Shaders/glowMapGeneration.vertex';
import '@babylonjs/core/Shaders/glowMapGeneration.fragment';
import '@babylonjs/core/Shaders/glowMapMerge.vertex';
import '@babylonjs/core/Shaders/glowMapMerge.fragment';
import '@babylonjs/core/Shaders/glowBlurPostProcess.fragment';
// **`kernelBlur` is the glow layer's *fourth* program, and it was the one still missing.**
//
// `GlowLayer` builds its blur out of `BlurPostProcess`, which is `kernelBlur` — not the
// `glowBlurPostProcess` above. Its shaders load through a dynamic `import()` in
// `thinBlurPostProcess.js`, so the same race applies, and its **vertex** shader is the only
// one in this scene with an include in it:
//
//     VERTEX SHADER ERROR: 0:20: '<' : syntax error      ← #include<kernelBlurVertex>
//
// Observed live in a browser, from a console left running during an unrelated measurement.
// The failure mode is quieter than a black world: the blur is a *post*-process, so the scene
// still draws, and only the bloom goes missing — every spell, portal and lit window silently
// stops glowing for that session. Two lines are enough: a shader module imports the includes
// its own source asks for, so naming `kernelBlurVertex` here as well would be dead weight.
import '@babylonjs/core/Shaders/kernelBlur.vertex';
import '@babylonjs/core/Shaders/kernelBlur.fragment';
import '@babylonjs/core/Shaders/postprocess.vertex';
import '@babylonjs/core/Shaders/outline.vertex';
import '@babylonjs/core/Shaders/outline.fragment';
// The environment BRDF lookup texture is decoded by an `rgbdDecode` post-process. Absent from this
// list, Babylon fetched the shader by URL, the dev stack's SPA fallback answered with `index.html`,
// and it failed on the `<` (`Uniforms: scale textureSampler`) on every load — harmless until
// something waited on that texture: a glTF body's PBR material did, and drew nothing but its
// shadow. The bodies now wear `StandardMaterial` (`skinned.ts`); this keeps the decode compiling.
import '@babylonjs/core/Shaders/rgbdDecode.fragment';
// The colour grade (`grading.ts`) — its includes come with it.
import '@babylonjs/core/Shaders/imageProcessing.fragment';

import { CAMERA_ALPHA, CAMERA_BETA, CAMERA_RADIUS } from './camera';

// Camera framing lives in `game/camera.ts`, which imports nothing: the minimap needs the
// yaw to rotate itself into screen space and must not pull Babylon in to read one angle.
// Re-exported here so existing importers do not have to care where it moved.
export { CAMERA_FOLLOW_RATE } from './camera';

export interface SceneHandles {
  engine: Engine;
  scene: Scene;
  camera: ArcRotateCamera;
  shadows: ShadowGenerator;
  /** Lights the zone renderer re-tints per biome. */
  ambient: HemisphericLight;
  sun: DirectionalLight;
  /** The pixel-art effect, or null when PIXELATE is off. */
  pixelArt: PostProcess | null;
  /** The colour grade and vignette, or null when `?grade=0` or safe mode removed it. */
  colourGrade: PostProcess | null;
  /**
   * Bloom for emissive surfaces, or null when `?glow=0` or safe mode refused it.
   *
   * **Opt-in per mesh.** A glow layer left to itself blooms every emissive material in the
   * scene, and characters carry an emissive lift purely so they stay readable against a
   * dark floor — bloom it and a player becomes a lamp. The zone renderer registers the
   * surfaces that are meant to be *light*: windows, wall trim and the portals.
   */
  glow: GlowLayer | null;
  dispose: () => void;
}

/**
 * Builds the whole scene: an isometric camera, a lit ground plane, and a shadow
 * generator entity meshes register with.
 *
 * The floor is not built here — see ZoneRenderer, which constructs the tiles and
 * re-tints these lights when a ZONE frame arrives.
 */
export function createScene(canvas: HTMLCanvasElement): SceneHandles {
  const engine = new Engine(canvas, true, { preserveDrawingBuffer: false, stencil: false });

  // Uniform buffers off.
  //
  // With UBOs enabled, Babylon prefixes its generated vertex shaders with a
  // global default layout qualifier:
  //
  //     layout(std140, column_major) uniform;
  //     uniform Scene { mat4 viewProjection; ... };
  //
  // That standalone `layout(...) uniform;` form is GLSL ES 3.1; it is not legal
  // in ES 3.00, which is what a WebGL2 context compiles. Lenient
  // implementations (ANGLE over SwiftShader, so every headless screenshot) accept
  // it. A strict driver rejects it with "VERTEX SHADER ERROR: 0:52" — and because
  // a material whose shader will not compile is silently skipped, the entire
  // world renders black while the DOM HUD carries on perfectly.
  //
  // Disabling UBOs makes Babylon emit plain uniforms instead. It costs a little
  // per-draw uniform traffic and buys a world that is actually visible.
  engine.disableUniformBuffers = true;

  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.04, 0.05, 0.08, 1);

  const camera = new ArcRotateCamera(
    'camera',
    CAMERA_ALPHA,
    CAMERA_BETA,
    // `?zoom=N` pulls the camera in N× for a browser check that needs to *see* a model.
    // Gameplay never sets it: the radius is a design constant and the HUD's projections
    // assume it. See `cameraZoomOverride`.
    CAMERA_RADIUS / cameraZoomOverride(),
    Vector3.Zero(),
    scene,
  );
  // The camera follows the player and nothing else; letting the pointer orbit it
  // would fight the click-to-move input on the same canvas.
  camera.detachControl();

  // Lighting is deliberately low. This is a dark ARPG: the ground should read
  // as near-black stone with a pool of light on it, not an evenly lit plane.
  // Anything brighter also washes out the title screen, which is a scrim over
  // this same scene.
  const ambient = new HemisphericLight('ambient', new Vector3(0, 1, 0), scene);
  ambient.intensity = 0.3;
  ambient.groundColor = new Color3(0.05, 0.05, 0.08);

  const sun = new DirectionalLight('sun', new Vector3(-0.6, -1, -0.4), scene);
  // The shadow window is set every frame by `followWithShadows`, not fitted to the casters.
  sun.autoUpdateExtends = false;
  sun.position = new Vector3(20, 30, 20);
  sun.intensity = 0.95;

  // 512 and unblurred on purpose. Each character is a couple of dozen separate
  // parts — they animate on independent joints, so they cannot be merged — and a
  // blurred 1024 map re-renders every one of them per frame. The pixel
  // post-process discards soft edges anyway, so the expensive filtering bought
  // nothing that survived to the screen.
  const shadows = new ShadowGenerator(512, sun);
  shadows.usePoissonSampling = false;
  shadows.darkness = 0.35;
  if (!renderFeatures.shadows) shadows.getShadowMap()?.dispose();
  followWithShadows(scene, sun, camera);

  // Exponential fog swallows the far edges of the floor, so a zone ends in
  // darkness instead of at a visible rectangular border. The colour and density
  // are the biome's; these are only the values before one is chosen.
  scene.fogMode = renderFeatures.fog ? Scene.FOGMODE_EXP2 : Scene.FOGMODE_NONE;
  scene.fogColor = new Color3(0.02, 0.022, 0.032);
  scene.fogDensity = 0.03;

  // No ground plane here: the floor is the zone's tiled geometry, built by
  // ZoneRenderer once the ZONE frame arrives. Until then the scene is empty
  // black, which is exactly what the title screen sits over.

  /**
   * **The windows were flat yellow paint, and this is what makes them light.**
   *
   * Owner's report, 2026-08-11: *visual monotony, missing depth, flat lighting.* The town's
   * windows, wall trim and portals have always been emissive, but an emissive surface with
   * nothing blooming it reads as a bright square rather than as a source — which is exactly
   * how a lit window differs from a painted one.
   *
   * A glow layer rather than a point light per window, and the difference is not subtle:
   * `StandardMaterial.maxSimultaneousLights` is 4, and the town merges into a handful of
   * meshes, so a light per window is not a cost question — it is not expressible. This is
   * one pass over the emissive channel at `blurKernelSize` and half resolution, independent
   * of how many windows there are.
   *
   * Deliberately below 1: at 1 the trim halos into a stripe and the scene stops reading as
   * dark. The point is contrast — cold stone, warm light — not brightness.
   */
  const glow = renderFeatures.glow
    ? (() => {
        const layer = new GlowLayer('glow', scene, { blurKernelSize: 24 });
        layer.intensity = 0.55;
        return layer;
      })()
    : null;

  logRenderFeatures();
  // **First in the chain**, so it outlines the scene as rendered and the grade darkens its corners
  // with everything else. See `outline.ts`.
  const outline = attachSilhouetteOutline(scene, camera);
  const pixelArt = attachPixelArt(camera);
  // After the pixel art, so the vignette's gradient is not quantised into rings.
  const colourGrade = attachColourGrade(camera);

  const onResize = () => engine.resize();
  window.addEventListener('resize', onResize);

  return {
    engine,
    scene,
    camera,
    shadows,
    ambient,
    sun,
    pixelArt,
    colourGrade,
    glow,
    dispose() {
      window.removeEventListener('resize', onResize);
      glow?.dispose();
      pixelArt?.dispose(camera);
      colourGrade?.dispose(camera);
      outline?.dispose();
      scene.dispose();
      engine.dispose();
    },
  };
}

/**
 * Half the width of the square the sun's shadow map covers, in world units, around what the camera
 * looks at. Chosen: the camera at `CAMERA_RADIUS` 26 shows about 40 units across, so 26 covers the
 * screen with a margin for tall casters just off it.
 */
export const SHADOW_HALF_EXTENT = 26;

/**
 * **The shadow window follows the camera** (2026-09-29), instead of Babylon's default of fitting
 * every shadow caster each frame.
 *
 * With the monsters' painted bodies (Phase 28.6) a crypt's casters reached 40 units off, and the
 * fitted window came out at −50…−5 along the light's X — **excluding the player at 0**. Everything
 * outside a shadow map reads as shadowed, so every room the player stood in went black while the
 * rooms around it stayed lit. Measured in the live game: 135 casters and a window that missed the
 * player; with `?mobskins=0`, 35 and a window round the player. A fixed square round the camera's
 * target cannot miss what is on screen, and the 512² map is spent on the visible 52 units instead
 * of on the whole zone.
 */
export function followWithShadows(scene: Scene, sun: DirectionalLight, camera: ArcRotateCamera): void {
  const back = sun.direction.normalizeToNew().scale(-60);
  sun.orthoLeft = -SHADOW_HALF_EXTENT;
  sun.orthoRight = SHADOW_HALF_EXTENT;
  sun.orthoTop = SHADOW_HALF_EXTENT;
  sun.orthoBottom = -SHADOW_HALF_EXTENT;
  sun.shadowMinZ = 1;
  sun.shadowMaxZ = 140;
  scene.onBeforeRenderObservable.add(() => {
    // The light's position is the centre of its orthographic window.
    camera.target.addToRef(back, sun.position);
  });
}

